import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    HubConnectionBuilder,
    LogLevel,
    type HubConnection,
} from '@microsoft/signalr';
import { AuthService, UserStore } from '@quipex/core/auth';
import { APP_CONFIG_TOKEN } from '@quipex/core/config';
import { MonitoringService } from '@quipex/core/logging';
import {
    BehaviorSubject,
    Subject,
    combineLatest,
    distinctUntilChanged,
    map,
    switchMap,
    takeUntil,
    timer,
} from 'rxjs';
import type {
    JobProgressMessage,
    RealtimeConnectionState,
    RealtimeDiagnostics,
} from './models';
import {
    JOB_PROGRESS_EVENT,
    buildHubUrl,
    isUsableConnectionState,
} from './realtime-connection';
import { stringifyRealtimeErrorDetail } from './realtime-error';
import {
    isKnownRealtimeJobType,
    normalizeJobProgressMessage,
} from './realtime-message-normalization';

const MAX_MESSAGES = 20;
const INITIAL_CONNECT_RETRY_DELAY_MS = 5000;

@Injectable({
    providedIn: 'root',
})
export class RealtimeService {
    //#region Dependency injection
    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    private readonly userStore = inject(UserStore);
    private readonly monitoringService = inject(MonitoringService);
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    //#endregion

    //#region State
    public readonly connectionState =
        signal<RealtimeConnectionState>('disconnected');
    public readonly messages = signal<readonly JobProgressMessage[]>([]);
    public readonly lastError = signal<string | null>(null);
    public readonly activeUserId = signal<string | null>(null);
    public readonly hubUrl = buildHubUrl(this.appConfig.apiBase);
    private readonly diagnosticsState = signal<RealtimeDiagnostics>(
        createRealtimeDiagnostics()
    );
    public readonly diagnostics = this.diagnosticsState.asReadonly();

    private connection: HubConnection | null = null;
    private operation = Promise.resolve();
    private readonly jobProgressSubject = new Subject<JobProgressMessage>();
    public readonly jobProgress$ = this.jobProgressSubject.asObservable();
    private readonly isEnabled$ = new BehaviorSubject(false);
    private readonly reconnectRetryRequested$ = new Subject<string>();
    private readonly cancelReconnectRetry$ = new Subject<void>();
    private reconnectEligibleUserId: string | null = null;
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        this.watchAuthenticatedSession();
        this.watchReconnectRetries();
    }
    //#endregion

    //#region Public methods
    public connect(): Promise<void> {
        this.isEnabled$.next(true);
        return this.operation;
    }

    public reconnect(): Promise<void> {
        return this.connectForCurrentUser(true);
    }

    public disconnect(): Promise<void> {
        this.isEnabled$.next(false);
        return this.operation;
    }

    public clearMessages(): void {
        this.messages.set([]);
    }
    //#endregion

    //#region Private methods
    private watchAuthenticatedSession(): void {
        combineLatest([
            this.isEnabled$,
            this.authService.isLoading$,
            this.authService.isAuthenticated$,
            this.userStore.me$,
        ])
            .pipe(
                map(([isEnabled, isLoading, isAuthenticated, user]) =>
                    isEnabled && !isLoading && isAuthenticated && user
                        ? user.id
                        : null
                ),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((userId) => {
                this.reconnectEligibleUserId = userId;

                void this.enqueue(async () => {
                    if (!userId) {
                        await this.stopConnection();
                        return;
                    }

                    await this.ensureConnectedForUser(userId);
                });
            });
    }

    private watchReconnectRetries(): void {
        this.reconnectRetryRequested$
            .pipe(
                switchMap((userId) =>
                    timer(INITIAL_CONNECT_RETRY_DELAY_MS).pipe(
                        takeUntil(this.cancelReconnectRetry$),
                        map(() => userId)
                    )
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((userId) => {
                void this.enqueue(async () => {
                    const user = this.userStore.me();

                    if (!user || user.id !== userId) {
                        return;
                    }

                    await this.ensureConnectedForUser(userId);
                });
            });
    }

    private connectForCurrentUser(forceRestart = false): Promise<void> {
        return this.enqueue(async () => {
            const user = this.userStore.me();
            if (!user) {
                await this.stopConnection();
                return;
            }

            await this.ensureConnectedForUser(user.id, forceRestart);
        });
    }

    private enqueue(operation: () => Promise<void>): Promise<void> {
        this.operation = this.operation.then(operation, operation);
        return this.operation;
    }

    private async ensureConnectedForUser(
        userId: string,
        forceRestart = false
    ): Promise<void> {
        this.clearReconnectRetry();

        if (
            !forceRestart &&
            this.connection &&
            this.activeUserId() === userId &&
            isUsableConnectionState(this.connection.state)
        ) {
            return;
        }

        await this.stopConnection();

        this.diagnosticsState.set(createRealtimeDiagnostics());
        this.connectionState.set('connecting');
        this.lastError.set(null);

        const connection = new HubConnectionBuilder()
            .withUrl(this.hubUrl, {
                accessTokenFactory: () => this.authService.getAccessToken(),
            })
            .withAutomaticReconnect()
            .configureLogging(
                this.appConfig.isProd ? LogLevel.Warning : LogLevel.Information
            )
            .build();

        this.registerConnectionHandlers(connection, userId);

        this.connection = connection;
        this.activeUserId.set(userId);

        try {
            await connection.start();
            this.connectionState.set('connected');
            this.diagnosticsState.update((diagnostics) => ({
                ...diagnostics,
                connectionId: connection.connectionId ?? null,
                connectedAtUtc: new Date().toISOString(),
            }));
        } catch (error) {
            this.recordError('Failed to connect to realtime hub', error);

            if (this.connection === connection) {
                this.connection = null;
                this.activeUserId.set(null);
                this.connectionState.set('error');
                this.scheduleReconnectRetry(userId);
            }

            await connection.stop().catch(() => undefined);
        }
    }

    private registerConnectionHandlers(
        connection: HubConnection,
        userId: string
    ): void {
        connection.on(JOB_PROGRESS_EVENT, (message: unknown) => {
            if (
                this.connection !== connection ||
                this.activeUserId() !== userId ||
                this.reconnectEligibleUserId !== userId
            ) {
                return;
            }

            const normalizedMessage = normalizeJobProgressMessage(message);

            if (!normalizedMessage) {
                this.diagnosticsState.update((diagnostics) => ({
                    ...diagnostics,
                    malformedMessageCount:
                        diagnostics.malformedMessageCount + 1,
                }));
                this.recordError('Received malformed realtime message', {
                    message,
                });
                return;
            }

            if (!isKnownRealtimeJobType(normalizedMessage.jobType)) {
                this.monitoringService.logEvent('RealtimeUnknownJobType', {
                    jobId: normalizedMessage.jobId,
                    jobType: normalizedMessage.jobType,
                    hubUrl: this.hubUrl,
                    timestamp: new Date().toISOString(),
                });
            }

            this.messages.update((messages) =>
                [normalizedMessage, ...messages].slice(0, MAX_MESSAGES)
            );
            this.diagnosticsState.update((diagnostics) => ({
                ...diagnostics,
                receivedMessageCount: diagnostics.receivedMessageCount + 1,
                lastMessageReceivedAtUtc: new Date().toISOString(),
            }));
            this.lastError.set(null);
            this.jobProgressSubject.next(normalizedMessage);
        });

        connection.onreconnecting((error) => {
            this.connectionState.set('reconnecting');
            this.diagnosticsState.update((diagnostics) => ({
                ...diagnostics,
                connectionId: null,
                reconnectCount: diagnostics.reconnectCount + 1,
            }));
            if (error) {
                this.recordError(
                    'Realtime hub reconnecting after error',
                    error
                );
            }
        });

        connection.onreconnected((connectionId) => {
            this.connectionState.set('connected');
            this.lastError.set(null);
            this.diagnosticsState.update((diagnostics) => ({
                ...diagnostics,
                connectionId: connectionId ?? null,
                connectedAtUtc: new Date().toISOString(),
            }));
        });

        connection.onclose((error) => {
            const isCurrentConnection = this.connection === connection;

            if (isCurrentConnection) {
                this.connectionState.set(error ? 'error' : 'disconnected');
                this.diagnosticsState.update((diagnostics) => ({
                    ...diagnostics,
                    connectionId: null,
                    disconnectedAtUtc: new Date().toISOString(),
                }));
            }

            if (error) {
                this.recordError('Realtime hub closed unexpectedly', error);
            }

            if (
                isCurrentConnection &&
                this.isEnabled$.value &&
                this.reconnectEligibleUserId === userId
            ) {
                this.scheduleReconnectRetry(userId);
            }
        });
    }

    private async stopConnection(): Promise<void> {
        this.clearReconnectRetry();

        const connection = this.connection;

        this.connection = null;
        this.activeUserId.set(null);
        this.connectionState.set('disconnected');
        this.lastError.set(null);
        this.messages.set([]);
        this.diagnosticsState.update((diagnostics) => ({
            ...diagnostics,
            connectionId: null,
            disconnectedAtUtc: connection
                ? new Date().toISOString()
                : diagnostics.disconnectedAtUtc,
        }));

        if (!connection) {
            return;
        }

        await connection.stop().catch((error) => {
            this.recordError('Failed to stop realtime hub connection', error);
        });
    }

    private scheduleReconnectRetry(userId: string): void {
        this.reconnectRetryRequested$.next(userId);
    }

    private clearReconnectRetry(): void {
        this.cancelReconnectRetry$.next();
    }

    private recordError(message: string, error: unknown): void {
        const detail =
            error instanceof Error
                ? error.message
                : typeof error === 'string'
                  ? error
                  : stringifyRealtimeErrorDetail(error);

        this.lastError.set(detail);
        this.monitoringService.logEvent('RealtimeError', {
            message,
            detail,
            hubUrl: this.hubUrl,
            timestamp: new Date().toISOString(),
        });
    }
    //#endregion
}

function createRealtimeDiagnostics(): RealtimeDiagnostics {
    return {
        connectionId: null,
        connectedAtUtc: null,
        disconnectedAtUtc: null,
        eventName: JOB_PROGRESS_EVENT,
        bufferLimit: MAX_MESSAGES,
        receivedMessageCount: 0,
        malformedMessageCount: 0,
        reconnectCount: 0,
        lastMessageReceivedAtUtc: null,
    };
}
